/**
 * TrackerGarmentTable
 *
 * Displays garment items within an expanded order row.
 * Shows SKU, product, size, color, type (POD/PAP), status badge,
 * and a three-dot action menu with "Reprint Label" option.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MoreHorizontal, Printer, Sliders, AlertTriangle, Plus, FileWarning, ArrowLeftRight } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ReprintLabelDialog } from './ReprintLabelDialog';
import { StatusOverrideDialog, type OverrideTarget } from './StatusOverrideDialog';
import { ReportGarmentIssueDialog } from '@/components/production/ReportGarmentIssueDialog';
import { useRole } from '@/hooks/use-role';
import { useLocation } from '@/contexts/LocationContext';
import { useStationSettings } from '@/features/production-settings/hooks/useStationSettings';
import type { PrintTechnology } from '@/features/production-orders/utils/print-tech-routing';
import { SwitchPrintTechnologyDialog, type SwitchPrintTechnologyTarget } from './SwitchPrintTechnologyDialog';
import { PRINT_TECHNOLOGY_SWITCH_REASON, printTechnologySwitchFor } from '../lib/print-technology-switch';
import { CLIENT_QC_REDO_REASON } from '../lib/client-qc-redo';

interface Garment {
  id: string;
  sku: string;
  productName: string;
  size: string | null;
  color: string | null;
  status: string;
  productionMethod: string;
  /** BLU-1412: optional so a stale cached payload offers no switch. */
  printTechnology?: PrintTechnology;
  /** BLU-1412: null on an unbatched garment, which the switch refuses. Optional so a stale cached payload offers no switch. */
  batchId?: string | null;
  isRedo: boolean;
  redoReason: string | null;
  lastActionAt: string | null;
  /**
   * BLU-817: server-computed — a bare SKU match would badge a sibling line
   * that shares this garment's shopify_sku but has its own valid artwork
   * (custom-premirror.ts:132-137), so this can only be resolved server-side.
   * Optional so a stale cached payload renders no badge rather than a wrong
   * one.
   */
  hasOpenPrintFileIssue?: boolean;
  printFileIssueReason?: string | null;
}

interface TrackerGarmentTableProps {
  garments: Garment[];
  orderId?: string;
  /**
   * The parent's `useCanManageFloor()`: the caller may make the floor owner's
   * calls here (BLU-1412 print technology switch). Computed once by the parent.
   */
  canManageFloor?: boolean;
}

const statusColors: Record<string, string> = {
  PENDING: 'bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-400',
  LABEL_PRINTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PICKED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  IN_PRETREAT: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  PRETREATED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  // Phase 50.01.5 — DTF: sky (film printed) + fuchsia (pressed). Non-colliding
  // with indigo (READY_TO_PACK/RELEASED), purple (CAUGHT), cyan/teal (pretreat).
  // The badge text (formatStatus) always renders the status, so colour is never
  // the only signal (DESIGN.md §16).
  FILM_PRINTED: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400',
  PRESSED: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-400',
  CAUGHT: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  IN_BIN: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  READY_TO_PACK: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  RELEASED: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  PACKED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  SHIPPED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  DISCARDED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  CANCELLED: 'bg-slate-100 text-slate-600 dark:bg-slate-800/30 dark:text-slate-500',
  // SUPERSEDED (2026-07-08): a re-batch replaced this sticker. Terminal but NOT a
  // failure — muted slate reads "dead/archived", deliberately distinct from the
  // red FAILED/DISCARDED and softer than CANCELLED.
  SUPERSEDED: 'bg-slate-100 text-slate-500 dark:bg-slate-800/40 dark:text-slate-400',
  INVENTORY_ERROR: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  EXTERNALLY_FULFILLED: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400',
};

/** Terminal/error statuses where reprinting does NOT make sense */
const NON_REPRINTABLE_STATUSES = new Set([
  'FAILED',
  'DISCARDED',
  'CANCELLED',
  'CANCELLED_RETURNED',
  // SUPERSEDED: the sticker is out of date — reprinting the OLD unit is wrong;
  // the live replacement is what gets (re)printed, from the scan dialog.
  'SUPERSEDED',
  'INVENTORY_ERROR',
  'EXTERNALLY_FULFILLED',
  'ORDER_BLOCKED',
  'PAP_DAMAGED',
]);

/** Pretty-print garment status. SUPERSEDED gets a friendlier, human label. */
function formatStatus(status: string, t: TFunction): string {
  if (status === 'SUPERSEDED') return t('tracker.garments.status.superseded');
  return status.replace(/_/g, ' ');
}

/** Maps garment status to a short verb for "Last Update" display */
const STATUS_TO_VERB: Record<string, string> = {
  LABEL_PRINTED: 'Printed',
  PICKED: 'Picked',
  IN_PRETREAT: 'Pretreat',
  PRETREATED: 'Pretreated',
  PRINTED: 'Printed',
  // Phase 50.01.5 — DTF verbs for the "Last Update" column.
  FILM_PRINTED: 'Film Printed',
  PRESSED: 'Pressed',
  CAUGHT: 'Caught',
  IN_BIN: 'Binned',
  READY_TO_PACK: 'Ready',
  RELEASED: 'Released',
  PACKED: 'Packed',
  SHIPPED: 'Shipped',
  FAILED: 'Failed',
  DISCARDED: 'Discarded',
  CANCELLED: 'Cancelled',
  SUPERSEDED: 'Replaced',
};

export function TrackerGarmentTable({ garments, orderId, canManageFloor = false }: TrackerGarmentTableProps) {
  const { t } = useTranslation('production');
  const [reprintTarget, setReprintTarget] = useState<{ id: string; sku: string; status: string } | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null);
  const [issueTarget, setIssueTarget] = useState<{ id: string; sku: string; productName: string } | null>(null);
  const [switchTarget, setSwitchTarget] = useState<SwitchPrintTechnologyTarget | null>(null);
  const { isAdmin, isWarehouseCoordinator, can } = useRole();
  const hasOverrideAccess = isAdmin() || isWarehouseCoordinator();
  // Both garment actions are productionStationProcedure (STATION_ROLES) — an
  // allowlist mirror, matching TrackerOrderTable's convention. A denylist like
  // !isCX() fails OPEN: Viewer is also outside STATION_ROLES and would still be
  // offered actions that 403.
  const canReportIssue = can('production.stations');
  const canUseReprint = can('production.stations');
  // BLU-1412: the switch is the floor owner's call. A client org viewing a 3PL
  // floor never sees it (the server's owner-floor fence refuses it anyway).
  const canSwitchTechnology = canManageFloor;
  const { selectedLocation } = useLocation();
  const { settings: stationSettings } = useStationSettings({ enabled: canSwitchTechnology });

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.sku')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.product')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.size')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.color')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.type')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.status')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.garments.header.updated')}</TableHead>
            <TableHead className="text-xs h-8 pb-1 w-8">
              <span className="sr-only">{t('tracker.garments.header.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {garments.map((g) => {
            const canReprint = canUseReprint && !NON_REPRINTABLE_STATUSES.has(g.status);
            const techSwitch = canSwitchTechnology
              ? printTechnologySwitchFor(g, { dtfEnabled: stationSettings.dtfEnabled })
              : null;
            // An always-visible kebab that opens an empty popover is worse than no
            // kebab — gating items individually made that reachable for CX.
            const hasGarmentActions = canReportIssue || canReprint || !!techSwitch || (hasOverrideAccess && !!orderId);

            return (
              <TableRow key={g.id} className="hover:bg-transparent group/garment">
                <TableCell className="py-1.5 font-mono text-xs">{g.sku}</TableCell>
                <TableCell className="py-1.5 text-xs max-w-[200px] truncate">{g.productName}</TableCell>
                <TableCell className="py-1.5 text-xs text-muted-foreground">{g.size ?? '-'}</TableCell>
                <TableCell className="py-1.5 text-xs text-muted-foreground">{g.color ?? '-'}</TableCell>
                <TableCell className="py-1.5">
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {g.productionMethod === 'PAP' ? 'PAP' : 'POD'}
                    </Badge>
                    {g.isRedo && (
                      g.redoReason === 'order_change_backfill' ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-400 text-orange-600 dark:text-orange-400 flex items-center gap-0.5">
                          <Plus className="h-2.5 w-2.5" aria-hidden="true" />
                          {t('orderChange.badge')}
                        </Badge>
                      ) : g.redoReason === PRINT_TECHNOLOGY_SWITCH_REASON ? (
                        // BLU-1412: a replacement for a garment switched before it
                        // was printed. Not a defect, so not the amber Redo pill.
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-400 text-blue-600 dark:text-blue-400 flex items-center gap-0.5">
                          <ArrowLeftRight className="h-2.5 w-2.5" aria-hidden="true" />
                          {g.printTechnology
                            ? t('printTechSwitch.badge', { tech: g.printTechnology })
                            : t('printTechSwitch.badgeGeneric')}
                        </Badge>
                      ) : (
                        // A defect redo. BLU-1462: a client QC redo (the client
                        // found the first one faulty after the hand over) keeps
                        // the amber pill and names its reason.
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 dark:text-amber-400">
                          {g.redoReason === CLIENT_QC_REDO_REASON ? t('clientQcRedo.badge') : 'Redo'}
                        </Badge>
                      )
                    )}
                    {/* BLU-817: an open print-file issue is a hard block — the
                        garment cannot be filmed/printed until it's resolved —
                        so this reads red (PRODUCTION_DESIGN.md §4.1), not the
                        amber Redo pill already in this cluster. The reason
                        sentence is a visible callout above the garment table,
                        never a title/tooltip (§10). */}
                    {g.hasOpenPrintFileIssue && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-400 text-red-600 dark:text-red-400 flex items-center gap-0.5">
                        <FileWarning className="h-2.5 w-2.5" aria-hidden="true" />
                        {t('tracker.printFileIssue.garmentBadge')}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-1.5">
                  <Badge
                    variant="secondary"
                    className={`text-[10px] ${statusColors[g.status] ?? statusColors.PENDING}`}
                  >
                    {formatStatus(g.status, t)}
                  </Badge>
                </TableCell>
                <TableCell className="py-1.5">
                  {g.lastActionAt ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-[10px] text-muted-foreground cursor-default whitespace-nowrap">
                          {STATUS_TO_VERB[g.status]
                            ? `${STATUS_TO_VERB[g.status]} ${formatDistanceToNow(new Date(g.lastActionAt), { addSuffix: true }).replace(' ago', '')}`
                            : formatDistanceToNow(new Date(g.lastActionAt), { addSuffix: true }).replace(' ago', '')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {format(new Date(g.lastActionAt), 'PPp')}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="py-1.5">
                  {hasGarmentActions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-60 group-hover/garment:opacity-100 focus-visible:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                        <span className="sr-only">{t('tracker.garments.garmentActions')}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* The dialog asks the backend which issue types are valid
                          for this garment's state. */}
                      {canReportIssue && (
                        <DropdownMenuItem
                          onClick={() => setIssueTarget({ id: g.id, sku: g.sku, productName: g.productName })}
                        >
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          {t('tracker.garments.reportIssue')}
                        </DropdownMenuItem>
                      )}
                      {canReprint && (
                        <>
                          {canReportIssue && <DropdownMenuSeparator />}
                          <DropdownMenuItem
                            onClick={() => setReprintTarget({ id: g.id, sku: g.sku, status: g.status })}
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            {t('tracker.garments.reprintLabel')}
                          </DropdownMenuItem>
                        </>
                      )}
                      {techSwitch && selectedLocation && (
                        <>
                          {(canReportIssue || canReprint) && <DropdownMenuSeparator />}
                          <DropdownMenuItem
                            onClick={() => setSwitchTarget({
                              garmentId: g.id,
                              sku: g.sku,
                              productName: g.productName,
                              status: g.status,
                              ...techSwitch,
                              locationId: selectedLocation.id,
                              orderId,
                              batchId: g.batchId,
                            })}
                          >
                            <ArrowLeftRight className="h-4 w-4 mr-2" />
                            {t('tracker.printTechSwitch.menuItem', { tech: techSwitch.to })}
                          </DropdownMenuItem>
                        </>
                      )}
                      {hasOverrideAccess && orderId && (
                        <>
                          {(canReportIssue || canReprint || !!techSwitch) && <DropdownMenuSeparator />}
                          <DropdownMenuItem
                            onClick={() => setOverrideTarget({
                              type: 'garment',
                              garmentId: g.id,
                              sku: g.sku,
                              currentStatus: g.status,
                              orderId,
                            })}
                          >
                            <Sliders className="h-4 w-4 mr-2" />
                            {t('tracker.garments.overrideStatus')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {reprintTarget && (
        <ReprintLabelDialog
          open={!!reprintTarget}
          onOpenChange={(open) => { if (!open) setReprintTarget(null); }}
          garmentId={reprintTarget.id}
          garmentSku={reprintTarget.sku}
          garmentStatus={reprintTarget.status}
        />
      )}

      {overrideTarget && (
        <StatusOverrideDialog
          open={!!overrideTarget}
          onOpenChange={(open) => { if (!open) setOverrideTarget(null); }}
          target={overrideTarget}
        />
      )}

      {switchTarget && (
        <SwitchPrintTechnologyDialog
          open={!!switchTarget}
          onOpenChange={(open) => { if (!open) setSwitchTarget(null); }}
          target={switchTarget}
        />
      )}

      {issueTarget && (
        <ReportGarmentIssueDialog
          open={!!issueTarget}
          onOpenChange={(open) => { if (!open) setIssueTarget(null); }}
          garmentUnitId={issueTarget.id}
          sku={issueTarget.sku}
          productName={issueTarget.productName}
          context="desk"
        />
      )}
    </>
  );
}
